import { useState, useEffect, useRef } from "react";
import emailjs from "@emailjs/browser";
import SmokeyCursor from "./SmokeyCursor";

// ─── EmailJS Config ────────────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID = "service_lp20g9e";
const EMAILJS_TEMPLATE_ID = "template_z92nhwd";
const EMAILJS_PUBLIC_KEY = "A5Uv43ac7hc3mthbU";

// ─── buildStyles ───────────────────────────────────────────────────────────────
function buildStyles(isDark) {
  return `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  :root {
    --primary:#3abefb;
    --secondary:#4f7cac;
    --gradient:linear-gradient(135deg,#3abefb 0%,#4f7cac 100%);
    --shadow:0 8px 32px rgba(58,190,251,0.15);
    --shadow-lg:0 25px 60px rgba(0,0,0,0.35);
    --transition:all 0.3s cubic-bezier(0.4,0,0.2,1);
    --font-head:'Syne',sans-serif;
    --font-body:'DM Sans',sans-serif;
    --radius:16px;
    --radius-lg:24px;
    --bg:        ${isDark ? "#0f1115" : "#f4f6f9"};
    --bg-2:      ${isDark ? "#171a21" : "#e9edf5"};
    --bg-3:      ${isDark ? "#1e222b" : "#ffffff"};
    --card-bg:   ${isDark ? "#1e222b" : "#ffffff"};
    --text:      ${isDark ? "#e6e6e6" : "#334155"};
    --text-dim:  ${isDark ? "#9aa0b5" : "#64748b"};
    --white:     ${isDark ? "#ffffff" : "#0f172a"};
    --border:    ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"};
    --border-p:  ${isDark ? "rgba(58,190,251,0.30)" : "rgba(58,190,251,0.35)"};
    --input-bg:  ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"};
    --scrolled-nav:${isDark ? "rgba(15,17,21,0.92)" : "rgba(244,246,249,0.94)"};
    --grid-line: ${isDark ? "rgba(58,190,251,0.05)" : "rgba(58,190,251,0.08)"};
    --icon-dim:  ${isDark ? "rgba(58,190,251,0.35)" : "rgba(58,190,251,0.22)"};
    --orbit-c:   ${isDark ? "rgba(58,190,251,0.18)" : "rgba(58,190,251,0.25)"};
  }

  *,*::before,*::after{margin:0;padding:0;box-sizing:border-box}
  html{scroll-behavior:smooth}
  body{font-family:var(--font-body);background:var(--bg);color:var(--text);line-height:1.7;overflow-x:hidden;transition:background .35s,color .35s}
  #portfolio-root{position:relative}

  .cursor{width:10px;height:10px;background:var(--primary);border-radius:50%;position:fixed;pointer-events:none;z-index:10000;top:0;left:0}
  .cursor-follower{width:32px;height:32px;border:1.5px solid var(--primary);border-radius:50%;position:fixed;pointer-events:none;z-index:9999;top:0;left:0}

  .navbar{position:fixed;top:0;left:0;width:100%;z-index:1000;padding:1.4rem 5%;transition:var(--transition)}
  .navbar.scrolled{background:var(--scrolled-nav);backdrop-filter:blur(22px);padding:.9rem 5%;border-bottom:1px solid var(--border);box-shadow:0 4px 24px rgba(0,0,0,.08)}
  .nav-container{display:flex;justify-content:space-between;align-items:center;max-width:1400px;margin:0 auto;gap:1rem}
  .logo{font-family:var(--font-head);font-size:1.7rem;font-weight:800;background:var(--gradient);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;cursor:pointer;flex-shrink:0}
  .nav-menu{display:flex;list-style:none;gap:2.2rem}
  .nav-link{font-size:.88rem;font-weight:500;color:var(--text-dim);text-decoration:none;position:relative;transition:var(--transition);cursor:pointer;letter-spacing:.03em}
  .nav-link:hover{color:var(--white)}
  .nav-link::after{content:'';position:absolute;bottom:-4px;left:0;width:0;height:1.5px;background:var(--gradient);transition:var(--transition)}
  .nav-link:hover::after{width:100%}
  .theme-toggle{flex-shrink:0}
  .toggle-track{width:52px;height:28px;border-radius:50px;cursor:pointer;position:relative;border:1px solid var(--border-p);background:${isDark ? "rgba(91,80,245,0.2)" : "rgba(91,80,245,0.12)"};transition:background .35s;outline:none}
  .toggle-thumb{position:absolute;top:3px;left:${isDark ? "25px" : "3px"};width:20px;height:20px;border-radius:50%;background:var(--gradient);display:flex;align-items:center;justify-content:center;font-size:.65rem;transition:left .3s cubic-bezier(.34,1.56,.64,1);box-shadow:0 2px 8px rgba(91,80,245,.4)}
  .hamburger{display:none;flex-direction:column;gap:5px;cursor:pointer;background:none;border:none;padding:4px;z-index:1002}
  .hamburger span{width:24px;height:1.5px;background:var(--text);transition:var(--transition);display:block}
  .hamburger.open span:nth-child(1){transform:rotate(45deg) translate(5px,5px)}
  .hamburger.open span:nth-child(2){opacity:0}
  .hamburger.open span:nth-child(3){transform:rotate(-45deg) translate(5px,-5px)}
  
  .hero{min-height:100vh;display:flex;align-items:center;padding:6rem 5% 3rem;position:relative;overflow:hidden}
  .hero-bg-grid{position:absolute;inset:0;z-index:0;background-image:linear-gradient(var(--grid-line) 1px,transparent 1px),linear-gradient(90deg,var(--grid-line) 1px,transparent 1px);background-size:60px 60px;-webkit-mask-image:radial-gradient(ellipse 80% 80% at 50% 50%,black 40%,transparent 100%);mask-image:radial-gradient(ellipse 80% 80% at 50% 50%,black 40%,transparent 100%)}
  .hero-glow{position:absolute;top:20%;right:10%;width:500px;height:500px;background:radial-gradient(circle,rgba(91,80,245,.10) 0%,transparent 70%);pointer-events:none;z-index:0}
  .hero-glow-2{position:absolute;bottom:10%;left:5%;width:300px;height:300px;background:radial-gradient(circle,rgba(6,214,160,.07) 0%,transparent 70%);pointer-events:none;z-index:0}
  .hero-content{display:grid;grid-template-columns:1fr 1fr;gap:4rem;max-width:1400px;margin:0 auto;align-items:center;position:relative;z-index:1}
  .hero-left{animation:heroIn .9s cubic-bezier(0.22,1,0.36,1) both}
  .hero-right{animation:heroIn .9s cubic-bezier(0.22,1,0.36,1) .18s both}
  @keyframes heroIn{from{opacity:0;transform:translateY(40px)}to{opacity:1;transform:none}}
  .hero-tag{display:inline-flex;align-items:center;gap:.5rem;font-size:.8rem;font-weight:500;color:var(--secondary);letter-spacing:.15em;text-transform:uppercase;background:rgba(6,214,160,.08);border:1px solid rgba(6,214,160,.2);padding:.4rem 1rem;border-radius:50px;margin-bottom:1.5rem}
  .hero-tag span{width:6px;height:6px;border-radius:50%;background:var(--secondary);animation:pulse 2s infinite}
  @keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(1.4)}}
  .hero-title{font-family:var(--font-head);font-size:4.2rem;font-weight:800;line-height:1.05;color:var(--white);margin-bottom:1.5rem}
  .hero-title .line{display:block}
  .hero-title .gw{background:var(--gradient);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
  .hero-desc{font-size:1.05rem;color:var(--text-dim);max-width:480px;margin-bottom:2.5rem;line-height:1.8}
  .hero-btns{display:flex;gap:1rem;flex-wrap:wrap}

  .btn{display:inline-flex;align-items:center;gap:.5rem;padding:.9rem 1.8rem;font-size:.92rem;font-weight:600;font-family:var(--font-body);text-decoration:none;border-radius:50px;transition:var(--transition);cursor:pointer;border:none;letter-spacing:.02em}
  .btn-primary{background:var(--gradient);color:#fff;box-shadow:0 8px 24px rgba(91,80,245,.35)}
  .btn-primary:hover{transform:translateY(-2px);box-shadow:0 12px 32px rgba(91,80,245,.5)}
  .btn-primary:disabled{opacity:0.7;cursor:not-allowed;transform:none}
  .btn-outline{background:transparent;color:var(--text);border:1px solid var(--border)}
  .btn-outline:hover{border-color:var(--primary);color:var(--white)}

  .hero-visual{position:relative;height:480px;display:flex;align-items:center;justify-content:center}
  .orbit{position:absolute;border-radius:50%;border:1px solid var(--orbit-c);animation:spin 20s linear infinite}
  .orbit-1{width:300px;height:300px;animation-duration:25s}
  .orbit-2{width:420px;height:420px;animation-duration:35s;animation-direction:reverse}
  .orbit-dot{position:absolute;top:-4px;left:50%;transform:translateX(-50%);width:8px;height:8px;border-radius:50%;background:var(--primary)}
  .orbit-dot-2{background:var(--secondary)}
  @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  .profile-box{width:240px;height:240px;position:relative;z-index:2;background:var(--bg-3);border-radius:50%;overflow:hidden;border:2px solid var(--border-p);box-shadow:0 30px 80px rgba(0,0,0,.25),0 0 0 8px ${isDark ? "rgba(58,190,251,0.08)" : "rgba(58,190,251,0.12)"};display:flex;align-items:center;justify-content:center}
  .profile-box img{width:100%;height:100%;object-fit:cover;display:block}
  .profile-box i{font-size:6rem;color:var(--icon-dim)}
  .float-badge{position:absolute;padding:.7rem 1.1rem;background:var(--bg-3);border:1px solid var(--border-p);border-radius:var(--radius);box-shadow:var(--shadow);display:flex;align-items:center;gap:.6rem;font-size:.8rem;font-weight:500;color:var(--white);z-index:3;animation:floatBadge 4s ease-in-out infinite}
  .float-badge-1{bottom:40px;right:-20px}
  .float-badge-2{top:40px;left:-20px;animation-delay:2s}
  .float-badge i{color:var(--primary);font-size:1rem}
  .float-badge .dot{width:8px;height:8px;border-radius:50%;background:var(--secondary)}
  @keyframes floatBadge{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}

  .stats{padding:2rem 5%;border-top:1px solid var(--border);border-bottom:1px solid var(--border);background:var(--bg-2)}
  .stats-container{max-width:1000px;margin:0 auto;display:grid;grid-template-columns:repeat(4,1fr);gap:1.25rem;align-items:center}
  .stat-item{text-align:center}
  .stat-number{font-family:var(--font-head);font-size:2.8rem;font-weight:800;background:var(--gradient);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;display:block}
  .stat-label{font-size:.8rem;color:var(--text-dim);text-transform:uppercase;letter-spacing:.1em}

  .section-header{text-align:center;margin-bottom:2.5rem}
  .section-tag{display:inline-block;font-size:.75rem;font-weight:600;text-transform:uppercase;letter-spacing:.15em;color:var(--primary);padding:.4rem 1rem;background:rgba(91,80,245,.1);border:1px solid rgba(91,80,245,.2);border-radius:50px;margin-bottom:.6rem}
  .section-title{font-family:var(--font-head);font-size:2.6rem;font-weight:800;color:var(--white);line-height:1.15}
  .section-title .hl{background:var(--gradient);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}

  .about{padding:4.5rem 5%;max-width:1400px;margin:0 auto}
  .about-content{display:grid;grid-template-columns:1fr 1.3fr;gap:3.5rem;align-items:center}
  .about-img-wrapper{border-radius:var(--radius-lg);background:var(--bg-3);border:1px solid var(--border-p);aspect-ratio:4/5;display:flex;align-items:center;justify-content:center;font-size:6rem;color:var(--icon-dim);position:relative;overflow:hidden}
  .about-img-wrapper img{width:100%;height:100%;object-fit:cover;display:block}
  .about-img-wrapper::after{content:'';position:absolute;inset:0;background:linear-gradient(135deg,transparent 60%,rgba(91,80,245,.05) 100%);pointer-events:none}
  .about-text p{color:var(--text-dim);margin-bottom:1rem;font-size:1rem;line-height:1.75}
  .about-text p strong{color:var(--white);font-weight:600}
  .skill-bars{margin:1.5rem 0 2rem}
  .skill-row{margin-bottom:1rem}
  .skill-row-top{display:flex;justify-content:space-between;margin-bottom:.4rem}
  .skill-row-top span:first-child{font-size:.9rem;font-weight:500;color:var(--white)}
  .skill-row-top span:last-child{font-size:.85rem;color:var(--text-dim)}
  .skill-bar-bg{height:5px;background:var(--border);border-radius:10px;overflow:hidden}
  .skill-bar-fill{height:100%;background:var(--gradient);border-radius:10px;width:0;transition:width 1.4s cubic-bezier(.4,0,.2,1)}

  .skills-section{padding:4.5rem 5%;background:var(--bg-2);position:relative;overflow:hidden}
  .skills-section::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 15% 50%,rgba(91,80,245,.06) 0%,transparent 50%),radial-gradient(circle at 85% 50%,rgba(6,214,160,.06) 0%,transparent 50%);pointer-events:none}
  .skills-container{max-width:1100px;margin:0 auto;display:flex;flex-direction:column;gap:1.25rem}
  .skills-category{background:var(--card-bg);border-radius:var(--radius-lg);padding:1.5rem;border:1px solid var(--border);transition:var(--transition)}
  .skills-category:hover{border-color:var(--border-p)}
  .category-title{font-family:var(--font-head);font-size:1.1rem;font-weight:700;color:var(--white);margin-bottom:1rem;display:flex;align-items:center;gap:.75rem}
  .category-title i{color:var(--primary)}
  .skills-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:.9rem}
  .skill-chip{background:var(--input-bg);border:1px solid var(--border);border-radius:var(--radius);padding:1rem .75rem;text-align:center;cursor:default;transition:all .35s cubic-bezier(.4,0,.2,1);position:relative;overflow:visible}
  .skill-chip:hover{background:rgba(91,80,245,.10);border-color:var(--primary);transform:translateY(-4px);box-shadow:0 8px 24px rgba(91,80,245,.2)}
  .skill-chip-text{font-size:.88rem;font-weight:500;color:var(--text)}
  .skill-chip:hover .skill-chip-text{color:var(--white)}
  .skill-chip-icon{position:absolute;top:-18px;left:50%;transform:translateX(-50%) scale(0);width:36px;height:36px;border-radius:10px;background:var(--gradient);display:flex;align-items:center;justify-content:center;font-size:1rem;color:#fff;opacity:0;transition:all .35s cubic-bezier(.34,1.56,.64,1);box-shadow:0 6px 16px rgba(91,80,245,.4)}
  .skill-chip:hover .skill-chip-icon{opacity:1;transform:translateX(-50%) scale(1)}

  .work{padding:4.5rem 5%;background:var(--bg)}
  .work-inner{max-width:1400px;margin:0 auto}
  .work-filters{display:flex;justify-content:center;gap:.75rem;margin-bottom:2rem;flex-wrap:wrap}
  .filter-btn{padding:.6rem 1.3rem;font-size:.85rem;font-weight:500;color:var(--text-dim);background:var(--card-bg);border:1px solid var(--border);border-radius:50px;cursor:pointer;transition:var(--transition);font-family:var(--font-body)}
  .filter-btn.active,.filter-btn:hover{background:var(--primary);color:#fff;border-color:var(--primary)}
  .work-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(340px,1fr));gap:1.75rem}
  .project-card{background:var(--card-bg);border-radius:var(--radius-lg);overflow:hidden;border:1px solid var(--border);transition:var(--transition)}
  .project-card:hover{transform:translateY(-6px) !important;box-shadow:var(--shadow-lg);border-color:var(--border-p)}
  .project-img{height:220px;background:var(--bg-2);display:flex;align-items:center;justify-content:center;position:relative;overflow:hidden}
  .project-img i{font-size:3.5rem;color:var(--icon-dim)}
  .project-overlay{position:absolute;inset:0;background:rgba(8,9,13,.85);display:flex;align-items:center;justify-content:center;gap:1rem;opacity:0;transition:var(--transition)}
  .project-card:hover .project-overlay{opacity:1}
  .project-overlay-btn{width:44px;height:44px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;color:#0f172a;border:none;cursor:pointer;transition:var(--transition);font-size:.95rem}
  .project-overlay-btn:hover{background:var(--primary);color:#fff}
  .project-info{padding:1.1rem 1.25rem}
  .project-cat{font-size:.75rem;font-weight:600;text-transform:uppercase;letter-spacing:.1em;color:var(--primary)}
  .project-info h3{font-family:var(--font-head);font-size:1.2rem;font-weight:700;color:var(--white);margin:.4rem 0 .7rem}
  .project-info p{font-size:.9rem;color:var(--text-dim);margin-bottom:1rem}
  .project-tech{display:flex;flex-wrap:wrap;gap:.4rem}
  .tech-tag{font-size:.75rem;padding:.25rem .7rem;background:rgba(91,80,245,.1);color:var(--primary);border-radius:50px}

  .github-section{padding:4.5rem 5%;background:var(--bg-2)}
  .github-inner{max-width:1100px;margin:0 auto}
  .github-card{background:var(--card-bg);border-radius:var(--radius-lg);border:1px solid var(--border);padding:1.75rem;transition:var(--transition)}
  .github-card:hover{border-color:var(--border-p)}
  .github-card-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:1.25rem;flex-wrap:wrap;gap:1rem}
  .gh-title-row{display:flex;align-items:center;gap:.85rem}
  .gh-title-row i{font-size:1.8rem;color:var(--white)}
  .gh-title-row h3{font-family:var(--font-head);font-size:1.2rem;font-weight:700;color:var(--white);margin-bottom:.15rem}
  .gh-subtitle{font-size:.83rem;color:var(--text-dim)}
  .gh-profile-btn{padding:.55rem 1.2rem;font-size:.82rem}
  .contrib-iframe-wrap{background:${isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)"};border:1px solid var(--border);border-radius:var(--radius);padding:1.5rem;margin-bottom:1.25rem}
  .contrib-note{font-size:.82rem;color:var(--text-dim);margin-bottom:1rem;display:flex;align-items:center;gap:.4rem}
  .contrib-note a{color:var(--primary);text-decoration:none}
  .contrib-img-box{width:100%;overflow:hidden;border-radius:8px}
  .contrib-heatmap-img{width:100%;height:auto;display:block;border-radius:8px}
  .gh-cards-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1rem;margin-top:.5rem}
  .gh-img-card{background:${isDark ? "rgba(13,17,23,0.9)" : "rgba(244,245,250,0.9)"};border:1px solid var(--border);border-radius:var(--radius);padding:.75rem;overflow:hidden;transition:var(--transition)}
  .gh-img-card:hover{border-color:var(--border-p);box-shadow:var(--shadow)}
  .gh-img-card img{width:100%;height:auto;display:block}

  .services{padding:4.5rem 5%;background:var(--bg)}
  .services-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:1.75rem;max-width:1100px;margin:0 auto}
  .service-card{background:var(--card-bg);padding:2rem 1.5rem;border-radius:var(--radius-lg);border:1px solid var(--border);text-align:center;transition:var(--transition)}
  .service-card:hover{transform:translateY(-6px) !important;border-color:var(--border-p);box-shadow:var(--shadow)}
  .service-icon{width:60px;height:60px;background:var(--gradient);border-radius:16px;display:flex;align-items:center;justify-content:center;margin:0 auto 1.1rem;font-size:1.4rem;color:#fff}
  .service-card h3{font-family:var(--font-head);font-size:1.2rem;font-weight:700;color:var(--white);margin-bottom:.75rem}
  .service-card p{font-size:.9rem;color:var(--text-dim);line-height:1.8}

  .contact{padding:4.5rem 5%;background:var(--bg-2)}
  .contact-inner{display:grid;grid-template-columns:1fr 1.6fr;gap:3rem;max-width:1100px;margin:0 auto}
  .contact-item{display:flex;align-items:center;gap:1.2rem;margin-bottom:1.25rem}
  .contact-icon-box{width:52px;height:52px;min-width:52px;background:rgba(91,80,245,.1);border:1px solid rgba(91,80,245,.2);border-radius:14px;display:flex;align-items:center;justify-content:center;font-size:1.1rem;color:var(--primary)}
  .contact-item h4{font-size:.8rem;text-transform:uppercase;letter-spacing:.1em;color:var(--text-dim);margin-bottom:.2rem}
  .contact-item p{color:var(--white);font-size:.95rem}
  .social-links{display:flex;gap:.75rem;margin-top:1.5rem}
  .social-link{width:42px;height:42px;border-radius:50%;background:var(--bg-3);border:1px solid var(--border);display:flex;align-items:center;justify-content:center;color:var(--text-dim);text-decoration:none;font-size:.95rem;transition:var(--transition)}
  .social-link:hover{background:var(--primary);color:#fff;border-color:var(--primary);transform:translateY(-2px)}
  .contact-form{background:var(--card-bg);padding:2rem;border-radius:var(--radius-lg);border:1px solid var(--border)}
  .form-group{position:relative;margin-bottom:1rem}
  .form-group input,.form-group textarea{width:100%;padding:1rem 1.2rem;font-size:.95rem;font-family:var(--font-body);color:var(--white);background:var(--input-bg);border:1px solid var(--border);border-radius:var(--radius);outline:none;transition:var(--transition);resize:none}
  .form-group input::placeholder,.form-group textarea::placeholder{color:var(--text-dim)}
  .form-group input:focus,.form-group textarea:focus{border-color:var(--primary);background:rgba(91,80,245,.05)}
  .contact-form .btn{width:100%;justify-content:center;margin-top:.5rem}
  .sent-msg{text-align:center;font-size:.95rem;margin-top:.75rem;padding:.75rem;border-radius:var(--radius);font-weight:500}
  .sent-msg.success{color:#10b981;background:rgba(16,185,129,0.08);border:1px solid rgba(16,185,129,0.2)}
  .sent-msg.error{color:#f43f5e;background:rgba(244,63,94,0.08);border:1px solid rgba(244,63,94,0.2)}
  @keyframes spin360{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  .fa-spin{animation:spin360 1s linear infinite}

  .footer{background:var(--bg);padding:1.75rem 5%;border-top:1px solid var(--border)}
  .footer-inner{max-width:1200px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:1rem}
  .footer p{font-size:.85rem;color:var(--text-dim)}
  .footer-links{display:flex;gap:.75rem}

  .btt{position:fixed;bottom:2rem;right:2rem;width:46px;height:46px;border-radius:50%;background:var(--gradient);border:none;color:#fff;font-size:1rem;cursor:pointer;z-index:999;opacity:0;pointer-events:none;transition:var(--transition);box-shadow:var(--shadow)}
  .btt.visible{opacity:1;pointer-events:auto}
  .btt:hover{transform:translateY(-3px)}

  @media(max-width:768px){
  .hamburger{display:flex}
  .nav-container{display:grid;grid-template-columns:1fr auto auto;align-items:center;gap:.75rem}
  .theme-toggle{order:2}
  .hamburger{order:3}
  .nav-menu{position:fixed;top:0;right:-100%;width:75%;height:100vh;background:var(--bg-2);flex-direction:column;justify-content:center;align-items:center;transition:var(--transition);z-index:1001;border-left:1px solid var(--border-p)}
  .nav-menu.open{right:0}
  .nav-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:1000}
  .nav-overlay.open{display:block}
  .hero-content{grid-template-columns:1fr;text-align:center}
  .hero-visual{display:flex;height:280px;margin-bottom:1rem}
  .orbit-1{width:200px;height:200px}
  .orbit-2{width:280px;height:280px}
  .profile-box{width:160px;height:160px}
  .float-badge{display:none}
  .hero-desc{margin:0 auto 2rem;max-width:100%}
  .hero-btns{justify-content:center}
  .hero-title{font-size:2.6rem}
  .stats-container{grid-template-columns:repeat(2,1fr)}
  .about-content{grid-template-columns:1fr}
  .contact-inner{grid-template-columns:1fr}
  .footer-inner{flex-direction:column;text-align:center}
  .section-title{font-size:2rem}
  .gh-cards-grid{grid-template-columns:1fr}
}
  @media(max-width:480px){
    .hero-title{font-size:2rem}
    .hero-btns{flex-direction:column}
    .btn{justify-content:center}
    .stats-container{grid-template-columns:1fr}
    .work-grid{grid-template-columns:1fr}
    .services-grid{grid-template-columns:1fr}
  }
`;
}

// ─── FOUC Fix ──────────────────────────────────────────────────────────────────
(function injectInitialStyles() {
  if (typeof document === "undefined") return;
  let el = document.getElementById("portfolio-styles");
  if (!el) { el = document.createElement("style"); el.id = "portfolio-styles"; document.head.appendChild(el); }
  el.textContent = buildStyles(true);
})();

// ─── useReveal ─────────────────────────────────────────────────────────────────
function useReveal(delay = 0) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight - 40 && rect.bottom > 0) {
      const t = setTimeout(() => setVisible(true), delay);
      return () => clearTimeout(t);
    }
    let timeout;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { timeout = setTimeout(() => setVisible(true), delay); observer.disconnect(); }
    }, { threshold: 0.1, rootMargin: "0px 0px -40px 0px" });
    observer.observe(el);
    return () => { observer.disconnect(); clearTimeout(timeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [ref, visible];
}

const HIDDEN = {
  up: { opacity: 0, transform: "translateY(18px)" },
  left: { opacity: 0, transform: "translateX(-24px)" },
  right: { opacity: 0, transform: "translateX(24px)" },
  scale: { opacity: 0, transform: "scale(0.95) translateY(12px)" },
};
const SHOWN = { opacity: 1, transform: "none" };
const TRANS = `opacity 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1)`;

function Reveal({ children, className = "", delay = 0, dir = "up", style = {} }) {
  const [ref, visible] = useReveal(delay);
  return <div ref={ref} className={className} style={{ ...style, ...(visible ? SHOWN : HIDDEN[dir]), transition: TRANS }}>{children}</div>;
}
function RevealAs({ as: Tag = "div", children, className = "", delay = 0, dir = "up", style = {}, ...rest }) {
  const [ref, visible] = useReveal(delay);
  return <Tag ref={ref} className={className} style={{ ...style, ...(visible ? SHOWN : HIDDEN[dir]), transition: TRANS }} {...rest}>{children}</Tag>;
}

// ─── GitHub ────────────────────────────────────────────────────────────────────
const GH_USER = "AwdheshSharma";
function ContribGraph({ isDark }) {
  const tc = isDark ? "c5c8d8" : "475569";
  const bg = isDark ? "0d1117" : "f4f5fa";
  const statsUrl = `https://github-readme-stats.vercel.app/api?username=${GH_USER}&show_icons=true&hide_border=true&bg_color=${bg}&title_color=5b50f5&text_color=${tc}&icon_color=06d6a0&border_radius=16`;
  const streakUrl = `https://streak-stats.demolab.com/?user=${GH_USER}&hide_border=true&background=${bg}&ring=5b50f5&fire=ff6b6b&currStreakLabel=5b50f5&sideLabels=5b50f5&dates=${tc}&currStreakNum=${isDark ? "ffffff" : "0f172a"}&sideNums=${isDark ? "ffffff" : "0f172a"}&border_radius=16`;
  const topLangsUrl = `https://github-readme-stats.vercel.app/api/top-langs/?username=${GH_USER}&layout=compact&hide_border=true&bg_color=${bg}&title_color=5b50f5&text_color=${tc}&border_radius=16`;
  return (
    <section className="github-section" id="github">
      <div className="github-inner">
        <Reveal className="section-header">
          <span className="section-tag">Activity</span>
          <h2 className="section-title">GitHub <span className="hl">Contributions</span></h2>
        </Reveal>
        <Reveal delay={120} style={{ display: "block" }} className="github-card">
          <div className="github-card-header">
            <div className="gh-title-row">
              <i className="fab fa-github" />
              <div><h3>{GH_USER}</h3><p className="gh-subtitle">Live stats & activity from GitHub</p></div>
            </div>
            <a href={`https://github.com/${GH_USER}`} target="_blank" rel="noreferrer" className="btn btn-outline gh-profile-btn">
              <i className="fab fa-github" /> View Profile
            </a>
          </div>
          <div className="contrib-iframe-wrap">
            <p className="contrib-note">
              <i className="fas fa-calendar-check" />&nbsp;Contribution heatmap — live from{" "}
              <a href={`https://github.com/${GH_USER}`} target="_blank" rel="noreferrer">github.com/{GH_USER}</a>
            </p>
            <div className="contrib-img-box">
              <img src={`https://ghchart.rshah.org/${GH_USER}`} alt="GitHub Contribution Graph" className="contrib-heatmap-img"
                style={{ filter: isDark ? "invert(1) hue-rotate(180deg) saturate(1.5) brightness(0.9)" : "none" }} />
            </div>
          </div>
          <div className="gh-cards-grid">
            {[statsUrl, streakUrl, topLangsUrl].map((src, i) => (
              <Reveal key={i} delay={i * 120} style={{ display: "block" }} className="gh-img-card">
                <img src={src} alt="GitHub Stats" onError={e => { e.target.style.display = "none"; }} />
              </Reveal>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Data ──────────────────────────────────────────────────────────────────────
const SKILLS = [
  {
    category: "Languages", icon: "fas fa-code", items: [
      { name: "Python", icon: "fab fa-python" }, { name: "C++", icon: "fas fa-cube" },
      { name: "HTML5", icon: "fab fa-html5" }, { name: "CSS3", icon: "fab fa-css3-alt" }, { name: "JavaScript", icon: "fab fa-js" },
    ]
  },
  {
    category: "Frameworks & Libraries", icon: "fas fa-layer-group", items: [
      { name: "Bootstrap", icon: "fab fa-bootstrap" }, { name: "Flask", icon: "fas fa-flask" },
      { name: "FastAPI", icon: "fas fa-bolt" }, { name: "React", icon: "fab fa-react" },
      { name: "React Native", icon: "fab fa-react" }, { name: "Django", icon: "fab fa-python" }, { name: "Streamlit", icon: "fas fa-stream" },
    ]
  },
  {
    category: "AI/ML & Data Science", icon: "fas fa-brain", items: [
      { name: "NumPy", icon: "fas fa-calculator" }, { name: "Pandas", icon: "fas fa-table" },
      { name: "Matplotlib", icon: "fas fa-chart-line" }, { name: "Scikit-learn", icon: "fas fa-microscope" }, { name: "TensorFlow", icon: "fas fa-network-wired" },
    ]
  },
  {
    category: "Databases & Tools", icon: "fas fa-database", items: [
      { name: "MySQL", icon: "fas fa-server" }, { name: "SQLite", icon: "fas fa-database" },
      { name: "Git", icon: "fab fa-git-alt" }, { name: "GitHub", icon: "fab fa-github" },
    ]
  },
];

const PROJECTS = [
  { id: 1, cat: "RAG", icon: "fas fa-radio", title: "Radio-Communication-AI-Assistant", desc: "AI agent specialized in radio communication knowledge using LangGraph, FastAPI, and Streamlit.", tech: ["Python", "LangGraph", "FastAPI", "Streamlit", "ChromaDB"], github: "https://github.com/AwdheshSharma/Radio-Communication-AI-Assistant", live: "/" },
  { id: 2, cat: "LLM", icon: "fas fa-microphone-alt", title: "Sara_Ai", desc: "SARA (Smart AI Response Assistant) is a voice-activated personal assistant.", tech: ["Python", "YtmusicApi", "SpeechRecognition", "adge_tts"], github: "https://github.com/AwdheshSharma/Sara_Ai", live: "" },
  { id: 3, cat: "web", icon: "fas fa-user-circle", image: "/projects/Portfolio.png", title: "my-portfolio", desc: "Modern developer portfolio with dark/light theme, smooth animations, EmailJS contact form, and GitHub stats integration.", tech: ["React", "EmailJS"], github: "https://github.com/AwdheshSharma/My_portfolio", live: "https://my-portfolio-ten-pi-49.vercel.app/" },
  { id: 4, cat: "LLM", icon: "fas fa-robot", title: "AI-INNOVATION-HUB", desc: "AI platform providing document summarization, sentiment analysis, speech-to-text transcription, and AI-powered image generation.", tech: ["Python", "Flask", "Hugging Face", "gemini", "SQLite"], github: "https://github.com/AwdheshSharma/AI-INNOVATION-HUB", live: "" },
  { id: 5, cat: "LLM", icon: "fas fa-headset", title: "A2A-customer-service-system", desc: "This project demonstrates a modular, multi-agent customer service system for an e-commerce platform.", tech: ["Python", "FastAPI", "gemini"], github: "https://github.com/AwdheshSharma/A2A-customer-service-system", live: "" },
  { id: 6, cat: "web", icon: "fas fa-map-marked-alt", title: "happy-journey", desc: "Web app to help users explore best destinations, calculate budgets, and manage bookings.", tech: ["HTML", "CSS", "JavaScript"], github: "https://github.com/AwdheshSharma/Happy-journey.github.io", live: "" },
];

const SERVICES = [
  { icon: "fas fa-code", title: "Python Development", desc: "Efficient and scalable Python solutions including automation, scripts, backend logic, and real-world problem-solving applications." },
  { icon: "fas fa-brain", title: "AI & Machine Learning", desc: "AI-powered solutions like sentiment analysis, text processing, image generation, and intelligent data-driven systems." },
  { icon: "fas fa-globe", title: "Web Development", desc: "Responsive and dynamic websites using HTML, CSS, JavaScript, and Python-based frameworks with clean UI." },
  { icon: "fas fa-database", title: "Backend & APIs", desc: "Secure backend systems, REST APIs, and database integration for scalable and reliable applications." },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────
function useCountUp(target, active) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let v = 0; const step = Math.ceil(target / 50);
    const t = setInterval(() => { v += step; if (v >= target) { setCount(target); clearInterval(t); } else setCount(v); }, 40);
    return () => clearInterval(t);
  }, [active, target]);
  return count;
}

function StatItem({ target, label, active, delay }) {
  const count = useCountUp(target, active);
  return (
    <RevealAs as="div" delay={delay} className="stat-item">
      <span className="stat-number">{count}+</span>
      <span className="stat-label">{label}</span>
    </RevealAs>
  );
}

function SkillBar({ name, pct, visible }) {
  return (
    <div className="skill-row">
      <div className="skill-row-top"><span>{name}</span><span>{pct}%</span></div>
      <div className="skill-bar-bg"><div className="skill-bar-fill" style={{ width: visible ? `${pct}%` : "0%" }} /></div>
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────
export default function Portfolio() {
  const [isDark, setIsDark] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [btt, setBtt] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const [aboutVisible, setAboutVisible] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);   // ← NEW
  const [sendError, setSendError] = useState(false);   // ← NEW

  const cursorRef = useRef(null);
  const followerRef = useRef(null);
  const mousePos = useRef({ x: 0, y: 0 });
  const followerPos = useRef({ x: 0, y: 0 });
  const rafIdRef = useRef(null);
  const statsRef = useRef(null);
  const aboutRef = useRef(null);

  useEffect(() => {
    let el = document.getElementById("portfolio-styles");
    if (!el) { el = document.createElement("style"); el.id = "portfolio-styles"; document.head.appendChild(el); }
    el.textContent = buildStyles(isDark);
  }, [isDark]);

  useEffect(() => {
    if (!document.querySelector('link[href*="font-awesome"]')) {
      const fa = document.createElement("link"); fa.rel = "stylesheet";
      fa.href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css";
      document.head.appendChild(fa);
    }
  }, []);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    document.body.style.cursor = "none";
    if (isDark) {
      if (cursorRef.current) cursorRef.current.style.display = "none";
      if (followerRef.current) followerRef.current.style.display = "none";
      if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = null; }
      return;
    }
    if (isMobile) return;
    if (cursorRef.current) cursorRef.current.style.display = "block";
    if (followerRef.current) followerRef.current.style.display = "block";
    const onMove = (e) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
      if (cursorRef.current) { cursorRef.current.style.left = e.clientX - 5 + "px"; cursorRef.current.style.top = e.clientY - 5 + "px"; }
    };
    const animate = () => {
      followerPos.current.x += (mousePos.current.x - followerPos.current.x) * 0.1;
      followerPos.current.y += (mousePos.current.y - followerPos.current.y) * 0.1;
      if (followerRef.current) { followerRef.current.style.left = followerPos.current.x - 16 + "px"; followerRef.current.style.top = followerPos.current.y - 16 + "px"; }
      rafIdRef.current = requestAnimationFrame(animate);
    };
    window.addEventListener("mousemove", onMove);
    rafIdRef.current = requestAnimationFrame(animate);
    return () => { window.removeEventListener("mousemove", onMove); if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = null; } };
  }, [isDark]);

  useEffect(() => {
    const h = () => { setScrolled(window.scrollY > 50); setBtt(window.scrollY > 300); };
    window.addEventListener("scroll", h); return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    if (!statsRef.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsVisible(true); }, { threshold: 0.3 });
    obs.observe(statsRef.current); return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!aboutRef.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setAboutVisible(true); }, { threshold: 0.2 });
    obs.observe(aboutRef.current); return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }); setMenuOpen(false); };

  // ─── EmailJS handleSubmit ────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setSendError(false);
    setSent(false);

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          from_email: form.email,
          subject: form.subject,
          message: form.message,
        },
        EMAILJS_PUBLIC_KEY
      );
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      console.error("EmailJS Error:", err);
      setSendError(true);
      setTimeout(() => setSendError(false), 5000);
    } finally {
      setSending(false);
    }
  };

  const NAV_ITEMS = ["home", "about", "skills", "work", "github", "services", "contact"];
  const filtered = PROJECTS.filter(p => filter === "all" || p.cat === filter);

  return (
    <div id="portfolio-root">
      {isDark && <SmokeyCursor simulationResolution={128} dyeResolution={1440} densityDissipation={3.5} velocityDissipation={2} pressure={0.1} pressureIterations={20} curl={3} splatRadius={0.2} splatForce={6000} enableShading colorUpdateSpeed={10} transparent intensity={1} autoColors />}

      <div className="cursor" ref={cursorRef} style={{ display: "none" }} />
      <div className="cursor-follower" ref={followerRef} style={{ display: "none" }} />

      <div className={`nav-overlay${menuOpen ? " open" : ""}`} onClick={() => setMenuOpen(false)} />

      {/* NAVBAR */}
      <nav className={`navbar${scrolled ? " scrolled" : ""}`}>
        <div className="nav-container">
          <span className="logo">AS.Dev</span>
          <ul className={`nav-menu${menuOpen ? " open" : ""}`}>
            {NAV_ITEMS.map(s => (
              <li key={s}><span className="nav-link" onClick={() => scrollTo(s)}>{s.charAt(0).toUpperCase() + s.slice(1)}</span></li>
            ))}
          </ul>
          <div className="theme-toggle">
            <button className="toggle-track" onClick={() => setIsDark(d => !d)}>
              <div className="toggle-thumb">{isDark ? "🌙" : "☀️"}</div>
            </button>
          </div>
          <button className={`hamburger${menuOpen ? " open" : ""}`} onClick={() => setMenuOpen(o => !o)}><span /><span /><span /></button>
        </div>
      </nav>

      {/* HERO */}
      <section id="home" className="hero">
        <div className="hero-bg-grid" />
        <div className="hero-glow" /><div className="hero-glow-2" />
        <div className="hero-content">
          <div className="hero-left">
            <div className="hero-tag"><span />&nbsp;Available for freelance work</div>
            <h1 className="hero-title">
              <span className="line">Building</span>
              <span className="line gw">Intelligent</span>
              <span className="line">Digital Solutions</span>
            </h1>
            <p className="hero-desc">I design and develop AI-powered applications, scalable backends, and modern web solutions that solve real-world problems.</p>
            <div className="hero-btns">
              <button className="btn btn-primary" onClick={() => scrollTo("work")}><i className="fas fa-briefcase" /> View Projects</button>
              <button className="btn btn-outline" onClick={() => scrollTo("contact")}>Contact Me</button>
            </div>
          </div>
          <div className="hero-visual hero-right">
            <div className="orbit orbit-1"><div className="orbit-dot" /></div>
            <div className="orbit orbit-2"><div className="orbit-dot orbit-dot-2" /></div>
            <div className="profile-box">
              <img src="/profile.png" alt="Awdhesh Sharma"
                onError={e => { e.target.style.display = "none"; e.target.nextElementSibling.style.display = "block"; }}
              />
              <i className="fas fa-user-tie" style={{ display: "none" }} />
            </div>
            <div className="float-badge float-badge-1"><i className="fas fa-brain" /> AI Developer</div>
            <div className="float-badge float-badge-2"><span className="dot" /> Open to Work</div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="stats" ref={statsRef}>
        <div className="stats-container">
          <StatItem target={15} label="Projects Built" active={statsVisible} delay={0} />
          <StatItem target={6} label="AI-Based Projects" active={statsVisible} delay={80} />
          <StatItem target={10} label="Technologies Used" active={statsVisible} delay={160} />
          <StatItem target={1000} label="Hours of Practice" active={statsVisible} delay={240} />
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="about" ref={aboutRef}>
        <Reveal className="section-header">
          <span className="section-tag">About Me</span>
          <h2 className="section-title">Passionate about building <span className="hl">intelligent</span> solutions</h2>
        </Reveal>
        <div className="about-content">
          <Reveal dir="left" className="about-img-wrapper">
            <img src="/about-photo.png" alt="Awdhesh Sharma"
              onError={e => { e.target.style.display = "none"; e.target.nextElementSibling.style.display = "block"; }}
            />
            <i className="fas fa-user-tie" style={{ display: "none" }} />
          </Reveal>
          <Reveal dir="right" delay={120} className="about-text">
            <p>Hello! I'm <strong>Awdhesh Sharma</strong>, a Computer Science Engineering graduate with a strong foundation in Python, Artificial Intelligence, and web development. I enjoy building practical solutions that solve real-world problems.</p>
            <p>I have hands-on experience working on AI-powered applications, backend systems, and modern web projects. I focus on writing clean, efficient code and continuously improving my skills.</p>
            <div className="skill-bars">
              {[{ name: "Python", pct: 90 }, { name: "AI / Machine Learning", pct: 80 }, { name: "HTML / CSS / JavaScript", pct: 85 }, { name: "Backend & APIs", pct: 75 }]
                .map(s => <SkillBar key={s.name} name={s.name} pct={s.pct} visible={aboutVisible} />)}
            </div>
            <a href="/AWDHESH_SHARMA_7489000590.pdf" download="Awdhesh_Sharma_Resume.pdf" className="btn btn-primary">
              <i className="fas fa-download" /> Download CV
            </a>
          </Reveal>
        </div>
      </section>

      {/* SKILLS */}
      <section id="skills" className="skills-section">
        <Reveal className="section-header">
          <span className="section-tag">Skills</span>
          <h2 className="section-title">My <span className="hl">Technical Expertise</span></h2>
        </Reveal>
        <div className="skills-container">
          {SKILLS.map((cat, ci) => (
            <Reveal key={cat.category} delay={ci * 90} style={{ display: "block" }} className="skills-category">
              <h3 className="category-title"><i className={cat.icon} /> {cat.category}</h3>
              <div className="skills-grid">
                {cat.items.map((sk, si) => (
                  <Reveal key={sk.name} dir="scale" delay={si * 55} style={{ display: "block" }} className="skill-chip">
                    <div className="skill-chip-icon"><i className={sk.icon} /></div>
                    <span className="skill-chip-text">{sk.name}</span>
                  </Reveal>
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WORK */}
      <section id="work" className="work">
        <div className="work-inner">
          <Reveal className="section-header">
            <span className="section-tag">Portfolio</span>
            <h2 className="section-title">Selected <span className="hl">Projects</span></h2>
          </Reveal>
          <Reveal delay={80} style={{ display: "flex", justifyContent: "center", gap: ".75rem", marginBottom: "3rem", flexWrap: "wrap" }}>
            {["all", "web", "RAG", "LLM"].map(f => (
              <button key={f} className={`filter-btn${filter === f ? " active" : ""}`} onClick={() => setFilter(f)}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </Reveal>
          <div className="work-grid">
            {filtered.map((p, i) => (
              <Reveal key={p.id} dir="scale" delay={i * 100} style={{ display: "block", overflow: "hidden", borderRadius: "var(--radius-lg)" }} className="project-card">
                <div className="project-img">
                  {p.image
                    ? <img src={p.image} alt={p.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <i className={p.icon} />
                  }
                  <div className="project-overlay">
                    {p.live && (
                      <a href={p.live} target="_blank" rel="noreferrer" className="project-overlay-btn" title="Live Demo">
                        <i className="fas fa-external-link-alt" />
                      </a>
                    )}
                    {p.github && (
                      <a href={p.github} target="_blank" rel="noreferrer" className="project-overlay-btn" title="GitHub">
                        <i className="fab fa-github" />
                      </a>
                    )}
                  </div>
                </div>
                <div className="project-info">
                  <span className="project-cat">{p.cat}</span>
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                  <div className="project-tech">{p.tech.map(t => <span key={t} className="tech-tag">{t}</span>)}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GITHUB */}
      <ContribGraph isDark={isDark} />

      {/* SERVICES */}
      <section id="services" className="services">
        <Reveal className="section-header">
          <span className="section-tag">Services</span>
          <h2 className="section-title">What I <span className="hl">Offer</span></h2>
        </Reveal>
        <div className="services-grid">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} dir="scale" delay={i * 100} style={{ display: "block" }} className="service-card">
              <div className="service-icon"><i className={s.icon} /></div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="contact">
        <Reveal className="section-header">
          <span className="section-tag">Contact</span>
          <h2 className="section-title">Let's Work <span className="hl">Together</span></h2>
        </Reveal>
        <div className="contact-inner">
          <Reveal dir="left">
            <div className="contact-item">
              <div className="contact-icon-box"><i className="fas fa-envelope" /></div>
              <div><h4>Email</h4><p>awdheshsharma590dev@gmail.com</p></div>
            </div>
            <div className="contact-item">
              <div className="contact-icon-box"><i className="fas fa-phone" /></div>
              <div><h4>Phone</h4><p>+91 7489000590</p></div>
            </div>
            <div className="contact-item">
              <div className="contact-icon-box"><i className="fas fa-map-marker-alt" /></div>
              <div><h4>Location</h4><p>Ujjain, Madhya Pradesh</p></div>
            </div>
            <div className="social-links">
              <a href="https://www.linkedin.com/in/awdhesh-sharma/" className="social-link" target="_blank" rel="noreferrer"><i className="fab fa-linkedin-in" /></a>
              <a href="https://github.com/AwdheshSharma" className="social-link" target="_blank" rel="noreferrer"><i className="fab fa-github" /></a>
              <a href="https://x.com/Awdhesh_13" className="social-link" target="_blank" rel="noreferrer"><i className="fab fa-twitter" /></a>
            </div>
          </Reveal>

          {/* ─── Contact Form with EmailJS ─── */}
          <RevealAs as="form" dir="right" delay={120} className="contact-form" onSubmit={handleSubmit}>
            {[["text", "Your Name", "name"], ["email", "Your Email", "email"], ["text", "Subject", "subject"]].map(([type, ph, key]) => (
              <div key={key} className="form-group">
                <input type={type} placeholder={ph} value={form[key]}
                  onChange={e => setForm({ ...form, [key]: e.target.value })} required disabled={sending} />
              </div>
            ))}
            <div className="form-group">
              <textarea placeholder="Your Message" rows={5} value={form.message}
                onChange={e => setForm({ ...form, message: e.target.value })} required disabled={sending} />
            </div>
            <button type="submit" className="btn btn-primary" disabled={sending}>
              {sending ? (
                <><i className="fas fa-spinner fa-spin" /> Sending...</>
              ) : sent ? (
                <><i className="fas fa-check" /> Sent!</>
              ) : (
                <><i className="fas fa-paper-plane" /> Send Message</>
              )}
            </button>
            {sent && <p className="sent-msg success">✓ Message sent! I’ll reply soon.</p>}
            {sendError && <p className="sent-msg error">✗ Something went wrong, try again or send an email.</p>}
          </RevealAs>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <Reveal style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", maxWidth: "1200px", margin: "0 auto" }}>
          <span className="logo">AS.Dev</span>
          <p>© 2025 Awdhesh Sharma. All rights reserved.</p>
          <div className="footer-links">
            <a href="https://www.linkedin.com/in/awdhesh-sharma/" className="social-link" target="_blank" rel="noreferrer"><i className="fab fa-linkedin-in" /></a>
            <a href="https://github.com/AwdheshSharma" className="social-link" target="_blank" rel="noreferrer"><i className="fab fa-github" /></a>
            <a href="https://x.com/Awdhesh_13" className="social-link" target="_blank" rel="noreferrer"><i className="fab fa-twitter" /></a>
          </div>
        </Reveal>
      </footer>

      <button className={`btt${btt ? " visible" : ""}`} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
        <i className="fas fa-arrow-up" />
      </button>
    </div>
  );
}